import graphqlClient from '../../../services/graphqlClient';
import getPlanrLinesQuery from '../graphql/getPlanrLinesQuery';
import * as R from 'ramda';
import createProductionCostMutation from '../graphql/createProductionCostMutation';
import importProductionCostPlanrDataMutation from '../graphql/importProductionCostPlanrDataMutation';
import productionCostBusesQuery from '../graphql/productionCostBusesQuery';
import productionCostProvidedValuesQuery from '../graphql/productionCostProvidedValuesQuery';
import productionCostEntryQuery from '../graphql/productionCostEntryQuery';
import getPlanrBusPartnersQuery from '../graphql/getPlanrBusPartners';
import getPlanrVehiclesQuery from '../graphql/getPlanrVehicles';
import {isNotNilOrEmpty} from 'ramda-adjunct';
import {
  PlanrBusPartner,
  PlanrLineSchedule,
  PlanrVehicle,
  ProductionCostFilterType
} from './types';
import getBusPartnerAndCountryCostEntriesQuery from '../graphql/getBusPartnerAndCountryCostEntriesQuery';
import {
  planrLinesFetchStarted,
  planrLinesFetchSucceeded,
  planrLinesFetchFailed,
  productionCostCreationIsPending,
  productionCostWasCreated,
  importProductionCostDataFailed,
  receivedImportProductionCostData,
  receivedProductionCostBuses,
  receivedProductionCost,
  receivedPlanrBusPartners,
  receivedPlanrVehicles,
  receivedPartnerAndCountryDriverCostForVehicle
} from './slice';
import {ProductionCostServerError} from '../components/types';

export const fetchPlanrLines = () => {
  return (dispatch, getState) => {
    const {productionCost} = getState();
    const {lines, linesLoading} = productionCost;

    // Only fetch if lines are missing AND not already loading
    if (lines.length > 0 || linesLoading) return;

    dispatch(planrLinesFetchStarted());

    graphqlClient
      .query({
        query: getPlanrLinesQuery
      })
      .then(result => {
        const {
          data: {getPlanrLines}
        } = result;

        return dispatch(planrLinesFetchSucceeded(getPlanrLines));
      })
      .catch(error => {
        console.error(error);
        dispatch(planrLinesFetchFailed());
      });
  };
};

// TODO: implement useCreateProductionCost hook similar to useCalculateProductionCost
export const createProductionCost = (
  filterType: ProductionCostFilterType,
  filteredIds: string[],
  callback: () => void
) => {
  return (dispatch, getState) => {
    const state = getState();

    dispatch(productionCostCreationIsPending());
    const productionCost = state.productionCost;
    let planrBusPartnerIdFiltersData = [];
    let planrVehicleIdFiltersData = [];
    if (isNotNilOrEmpty(filteredIds)) {
      if (ProductionCostFilterType.PlanrBusPartners === filterType) {
        const planrBusPartners = state.productionCost?.planrBusPartners;
        planrBusPartnerIdFiltersData = getPlanrBusPartnerIdFiltersData(
          planrBusPartners,
          filteredIds
        );
      } else if (ProductionCostFilterType.PlanrVehicles === filterType) {
        const planrVehicles = state.productionCost?.planrVehicles;
        planrVehicleIdFiltersData = getPlanrVehicleIdFiltersData(
          planrVehicles,
          filteredIds
        );
      }
    }

    const data = R.pick(
      ['name', 'startDate', 'endDate', 'status', 'costYear'],
      productionCost
    );

    graphqlClient
      .mutate({
        mutation: createProductionCostMutation,
        variables: {
          ...data,
          startDate: formateProductionCostDate(data.startDate),
          endDate: formateProductionCostDate(data.endDate),
          planrBusPartnerIdFilters: planrBusPartnerIdFiltersData,
          planrVehicleIdFilters: planrVehicleIdFiltersData
        }
      })
      .then(result => {
        const {
          data: {createProductionCostEntry}
        } = result;
        dispatch(productionCostWasCreated(createProductionCostEntry.id));
        dispatch(importProductionCostData(callback));
      });
  };
};

const importProductionCostData = (callback: () => void) => {
  return (dispatch, getState) => {
    const state = getState();

    const productionCost = state.productionCost;

    const scheduleIds = R.pipe(
      R.pluck('schedules'),
      R.map(R.filter(x => x.selected)),
      R.map(R.pluck('uuid')),
      R.flatten
    )(productionCost.lineSchedules);

    graphqlClient
      .mutate({
        mutation: importProductionCostPlanrDataMutation,
        variables: {
          id: productionCost.id,
          scheduleIds
        },
        errorPolicy: 'all'
      })
      .then(result => {
        const {
          data: {importProductionCostPlanrData},
          errors
        } = result;

        if (errors) {
          dispatch(
            importProductionCostDataFailed(
              errors as any as ProductionCostServerError[]
            )
          );
        } else {
          dispatch(
            receivedImportProductionCostData(importProductionCostPlanrData)
          );
          callback();
        }
      })
      .catch(error => {
        const message = error.message ?? error;
        dispatch(
          importProductionCostDataFailed([
            {
              id: 'unknown_error',
              message
            } as ProductionCostServerError
          ])
        );
      });
  };
};

export const getProductionCostBuses = (productionCostEntryId: string) => {
  return dispatch => {
    graphqlClient
      .query({
        query: productionCostBusesQuery,
        variables: {
          productionCostEntryId
        }
      })
      .then(result => {
        const {
          data: {productionCostBuses}
        } = result;

        return dispatch(receivedProductionCostBuses(productionCostBuses));
      });
  };
};

export const fetchPartnerAndCountryDriverCost = (
  busPartnerId: string,
  countryId: string,
  productionCostYear: number,
  entryIds: string[]
) => {
  return dispatch => {
    graphqlClient
      .query({
        query: getBusPartnerAndCountryCostEntriesQuery,
        variables: {busPartnerId, countryId, year: productionCostYear},
        fetchPolicy: 'no-cache',
        errorPolicy: 'all'
      })
      .then(result => {
        const {
          data: {getBusPartnerCostEntryYear, countryCostEntry},
          errors
        } = result;

        if (errors) {
          const unexpectedErrors = errors.filter(
            error => error.extensions?.errorType !== 'ResourceNotFound'
          );
          if (unexpectedErrors.length) {
            unexpectedErrors.forEach(e => {
              console.error('Error fetching BP and country driver cost: ', e);
            });
            return;
          }
        }

        dispatch(
          receivedPartnerAndCountryDriverCostForVehicle({
            busPartnerId,
            countryId,
            productionCostYear,
            entryIds,
            busPartnerDriverCostEntry:
              getBusPartnerCostEntryYear?.driverCostEntry,
            countryDriverCostEntry: countryCostEntry?.driverCostEntry
          })
        );
      });
  };
};

export const importProductionCostSchedules = (
  productionCostEntryId: string
) => {
  return dispatch => {
    graphqlClient
      .query({
        query: productionCostProvidedValuesQuery,
        variables: {
          productionCostEntryId
        },
        fetchPolicy: 'no-cache'
      })
      .then(result => {
        const {
          data: {productionCostProvidedValues}
        } = result;

        dispatch(
          receivedImportProductionCostData(productionCostProvidedValues)
        );
      });
  };
};

export const getProductionCost = (productionCostEntryId: string) => {
  return dispatch => {
    graphqlClient
      .query({
        query: productionCostEntryQuery,
        variables: {
          productionCostEntryId
        },
        fetchPolicy: 'no-cache'
      })
      .then(result => {
        const {
          data: {productionCostEntry}
        } = result;
        dispatch(importProductionCostSchedules(productionCostEntryId));
        dispatch(receivedProductionCost(productionCostEntry));
      });
  };
};

export const formateProductionCostDate = (date: string | Date) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  const month = ('0' + (dateObj.getMonth() + 1)).slice(-2);
  const day = ('0' + dateObj.getDate()).slice(-2);

  return `${dateObj.getFullYear()}-${month}-${day}`;
};

export const fetchPlanrBusPartners = () => {
  return (dispatch, getState) => {
    const state = getState();
    const lineSchedules = state.productionCost?.lineSchedules;

    const scheduleIds = getScheduleIds(lineSchedules);

    graphqlClient
      .query({
        query: getPlanrBusPartnersQuery,
        variables: {
          scheduleIds
        }
      })
      .then(result => {
        const {
          data: {getPlanrBusPartners}
        } = result;

        return dispatch(receivedPlanrBusPartners(getPlanrBusPartners));
      });
  };
};

export const fetchPlanrVehicles = () => {
  return (dispatch, getState) => {
    const state = getState();
    const lineSchedules = state.productionCost?.lineSchedules;

    const scheduleIds = getScheduleIds(lineSchedules);

    graphqlClient
      .query({
        query: getPlanrVehiclesQuery,
        variables: {
          scheduleIds
        }
      })
      .then(result => {
        const {
          data: {getPlanrVehicles}
        } = result;

        return dispatch(receivedPlanrVehicles(getPlanrVehicles));
      });
  };
};

const getScheduleIds = (lineSchedules: PlanrLineSchedule[]) =>
  R.pipe(
    R.pluck('schedules'),
    R.map(R.filter(x => x.selected)),
    R.map(R.pluck('uuid')),
    R.flatten
  )(lineSchedules);

const getPlanrBusPartnerIdFiltersData = (
  planrBusPartners: PlanrBusPartner[],
  filteredPlanrBusPartnerIds: string[]
) => {
  const planrBusPartnerIdFiltersData = R.innerJoin(
    (busPartner: PlanrBusPartner, filteredBusPartnerId: string) =>
      busPartner.busPartnerMobilityId === filteredBusPartnerId,
    planrBusPartners ?? [],
    filteredPlanrBusPartnerIds ?? []
  );
  return planrBusPartnerIdFiltersData.map(
    ({busPartnerMobilityId, busPartnerName}) => ({
      planrMobilityPartnerId: busPartnerMobilityId,
      planrBusPartnerName: busPartnerName
    })
  );
};

const getPlanrVehicleIdFiltersData = (
  planrVehicles: PlanrVehicle[],
  filteredPlanrVehicleIds: string[]
) => {
  const planrVehicleIdFiltersData = R.innerJoin(
    (planrVehicle, filteredPlanrVehicleId) =>
      planrVehicle.id === filteredPlanrVehicleId,
    planrVehicles ?? [],
    filteredPlanrVehicleIds ?? []
  );
  return planrVehicleIdFiltersData.map(({id, name}) => ({
    planrVehicleId: id,
    planrVehicleName: name
  }));
};
